import { Link } from "@inertiajs/react";
import Layout from "../Layout";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/marketing/FAQ";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";
import {
  Check,
  Building2,
  Zap,
  Eye,
  ArrowUpCircle,
  Shield,
  GitPullRequest,
  TrendingDown,
  MessageSquare,
  Bot,
  AlertTriangle,
} from "lucide-react";
import { SiGithub } from "@icons-pack/react-simple-icons";

const FREE_FEATURES = [
  "3 free PRs per workspace (lifetime)",
  "Unlimited savings recommendations",
  "Invite teammates",
  "Upgrade to Pro from inside Infralyst at any time",
] as const;

const PRO_FEATURES = [
  "Unlimited PRs",
  "Everything in Free",
  "Priority email support",
  "30-day money-back guarantee",
] as const;

const BILLING_ITEMS = [
  {
    icon: Building2,
    title: "Per-workspace billing",
    description:
      "Each workspace has its own plan. You can keep some workspaces on Free and upgrade others to Infralyst Pro.",
  },
  {
    icon: Zap,
    title: "Free PRs",
    description:
      "On Free, every workspace gets 3 PRs. Your free PRs never expire.",
  },
  {
    icon: Eye,
    title: "When you use your 3 free PRs",
    description:
      "After you've used all 3 free PRs, you can still see savings recommendations. To generate more PRs for that workspace, upgrade it to Infralyst Pro.",
  },
  {
    icon: ArrowUpCircle,
    title: "What counts as one PR",
    description:
      "One PR equals one Terraform pull request that Infralyst generates in your repo. Whether it rightsizes an instance, migrates a resource, or removes something unused, it counts as one PR.",
  },
] as const;

const PLATFORM_FEATURES = [
  {
    icon: SiGithub,
    text: "Connect unlimited AWS accounts, GitHub repos and Terraform states",
  },
  {
    icon: Shield,
    text: "Read-only AWS access via the Infralyst Terraform module and IAM role",
  },
  {
    icon: GitPullRequest,
    text: "PR-only workflow in your own repos. Your team reviews and merges every change.",
  },
  {
    icon: TrendingDown,
    text: "Recommendations based on real usage data and fixed thresholds. No machine learning, no guessing.",
  },
  {
    icon: Bot,
    text: "AI agents write the Terraform code. Your team reviews every diff before it ships.",
  },
  {
    icon: AlertTriangle,
    text: "PRs flag changes that require downtime so your team can plan a maintenance window.",
  },
  {
    icon: MessageSquare,
    text: "Optional Slack notifications so new savings show up where your team already works",
  },
] as const;

const VALUE_ITEMS = [
  {
    title: "Real savings without more manual work",
    description:
      "Infralyst runs the analysis and generates the Terraform changes. Your engineers stay focused on product work.",
  },
  {
    title: "Conservative by default",
    description:
      "Recommendations use real usage data and conservative thresholds. When the data is sparse or noisy, Infralyst skips the recommendation rather than guessing.",
  },
  {
    title: "Fits how you already ship",
    description:
      "Every change arrives as a Terraform PR in your own repo. Your team reviews, tests and merges it like any other change.",
  },
] as const;

const FAQ_ITEMS = [
  {
    question:
      "What's the difference between flat-rate and percentage-of-savings pricing for AWS cost tools?",
    answer:
      "Some tools charge a percentage of your savings, which means the more you save, the more you pay. Infralyst Pro is a flat $99 per month per workspace, regardless of how many AWS accounts you connect or how much you save. Your savings are yours.",
  },
  {
    question: "How much can I expect to save with automated AWS rightsizing?",
    answer:
      "Most AWS accounts have at least a few oversized instances. A single EC2 rightsize from an m5.xlarge to an m5.large saves roughly $70 per month. Infralyst Pro costs $99 per month per workspace, so one or two rightsizing changes typically cover the subscription.",
  },
  {
    question: "Does Infralyst charge based on AWS spend or number of resources?",
    answer:
      "No. Infralyst Pro is a flat $99 per month per workspace. You can connect as many AWS accounts, GitHub repos, and Terraform states as you need. The price stays the same whether you have 10 instances or 10,000.",
  },
  {
    question: "What if Infralyst doesn't work for us?",
    answer:
      "Infralyst Pro includes a 30-day money-back guarantee. If you don't see value in the first month, email hello@infralyst.io for a full refund. No questions asked. You can also try the Free plan first, which includes 3 PRs per workspace with no credit card required.",
  },
  {
    question: "What's the ROI of automated AWS rightsizing compared to doing it manually?",
    answer:
      "Manual rightsizing means pulling CloudWatch data, cross-referencing pricing, choosing an instance type, and editing Terraform files by hand, per resource, per region, per account. At $99/month, Infralyst pays for itself with a single resize. Teams with hundreds of resources save thousands of dollars per month without dedicating engineering time to the analysis.",
  },
  {
    question: "Do I need a paid cost optimization tool if I already use Terraform?",
    answer:
      "Terraform manages your infrastructure but doesn't tell you when something is oversized. You could do the analysis and code changes yourself, but it takes hours per resource. Infralyst watches utilization across all your accounts and generates the Terraform PRs automatically. The Free plan lets you try it with 3 PRs before committing.",
  },
  {
    question: "What's included in the Free plan vs Pro?",
    answer:
      "The Free plan includes full analysis across all your AWS accounts and regions, unlimited recommendations, and 3 Terraform PRs per workspace. Pro unlocks unlimited PR generation, Slack notifications, and priority support for $99 per month. Both plans support unlimited AWS accounts and GitHub repos.",
  },
  {
    question: "How much does AWS cost optimization tooling typically cost?",
    answer:
      "Enterprise cost optimization platforms typically charge 1-5% of managed spend, which can be thousands per month for large accounts. Lighter tools range from free tiers to $200-500/month. Infralyst is a flat $99/month per workspace with no percentage-of-savings fees, so your costs are predictable and your savings are yours.",
  },
  {
    question: "Can I try Infralyst before paying?",
    answer:
      "Yes. The Free plan requires no credit card and includes full AWS analysis, unlimited recommendations, and 3 Terraform PRs per workspace. You can connect all your AWS accounts, see every savings opportunity, and generate PRs to verify the quality before upgrading.",
  },
  {
    question: "How does pricing work with multiple AWS accounts or teams?",
    answer:
      "A workspace is the billing unit. One workspace can connect unlimited AWS accounts, GitHub repos, and Terraform states. If you need separate workspaces for different teams or billing entities, each workspace is $99/month on Pro. Most teams start with one workspace covering their entire AWS organization.",
  },
] as const;

function PricingHero(): React.ReactElement {
  return (
    <section className="px-4 py-16 pt-40">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          Simple, transparent <span className="text-primary">pricing</span>
        </h1>
        <p className="max-w-2xl mx-auto mb-8 text-xl leading-relaxed text-surface-foreground/80">
          Every workspace starts free with 3 PRs. Upgrade to Infralyst Pro from
          inside Infralyst when you need more.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg">
            <Link href="/signup">Start free with 3 PRs</Link>
          </Button>
          <Button variant="outline" size="lg" asChild>
            <a href={CALENDLY_COST_REVIEW_URL} target="_blank" rel="noopener noreferrer">
              Book a demo
            </a>
          </Button>
        </div>
        <p className="text-sm text-surface-foreground/60 mt-4">
          No credit card required · Read-only IAM role · 30-day money-back
          guarantee
        </p>
      </div>
    </section>
  );
}

function PlanCards(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <div className="rounded-3xl border bg-background/50 p-6 flex flex-col hover:border-primary/20 hover:bg-primary/5 transition-colors">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-1">Free</h2>
              <div className="text-3xl font-extrabold mb-2">
                $0
                <span className="text-base text-surface-foreground/60 font-normal">
                  {" "}
                  / workspace
                </span>
              </div>
              <p className="text-sm text-surface-foreground/80">
                Perfect for trying out Infralyst.
              </p>
            </div>

            <ul className="space-y-3 flex-1 mb-4">
              {FREE_FEATURES.map((feature) => (
                <li key={feature} className="flex items-start text-sm">
                  <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <p className="text-xs text-surface-foreground/50">
              Best if you want to pilot Infralyst on one or more AWS accounts.
            </p>
          </div>

          {/* Infralyst Pro Plan */}
          <div className="rounded-3xl border-2 border-primary bg-background/50 p-6 flex flex-col hover:bg-primary/5 transition-colors">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-1">Infralyst Pro</h2>
              <div className="text-3xl font-extrabold mb-2">
                $99
                <span className="text-base text-surface-foreground/60 font-normal">
                  {" "}
                  / month · per workspace
                </span>
              </div>
              <p className="text-sm text-surface-foreground/80">
                For teams that want automated AWS cost optimization in their
                Terraform workflow.
              </p>
            </div>

            <ul className="space-y-3 flex-1 mb-4">
              {PRO_FEATURES.map((feature, index) => (
                <li key={feature} className="flex items-start text-sm">
                  <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className={index === 0 ? "font-semibold" : ""}>
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <p className="text-xs text-surface-foreground/50">
              Keep some workspaces on Free and upgrade others to Infralyst Pro
              as you need.
            </p>
          </div>
        </div>

        <p className="text-xs text-surface-foreground/50 text-center mt-8">
          Billing is <span className="font-medium">per workspace</span>. Business
          customers only. Prices exclude tax.
        </p>
      </div>
    </section>
  );
}

function HowBillingWorks(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          How billing and free PRs work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {BILLING_ITEMS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex gap-4">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon className="size-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1">{title}</h3>
                <p className="text-sm text-surface-foreground/80 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EverythingYouGet(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          Everything you get on <span className="text-primary">every plan</span>
        </h2>
        <div className="space-y-4">
          {PLATFORM_FEATURES.map(({ icon: Icon, text }) => (
            <div
              key={text}
              className="flex items-start gap-4 p-4 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon className="size-5" />
              </div>
              <p className="text-surface-foreground/80 leading-relaxed pt-2">
                {text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyProPaysForItself(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-6">
          Why Infralyst Pro often{" "}
          <span className="text-primary">pays for itself</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          In most AWS environments, a few conservative optimizations save more
          per month than Infralyst Pro costs. Infralyst keeps watching for new
          savings so your team doesn't have to.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {VALUE_ITEMS.map(({ title, description }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <h3 className="text-lg font-bold mb-3">{title}</h3>
              <p className="text-sm text-surface-foreground/80 leading-relaxed">
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl font-bold mb-6">
          Ready to see what <span className="text-primary">Infralyst</span> finds
          in your AWS accounts?
        </h2>
        <p className="text-surface-foreground/80 mb-8 max-w-2xl mx-auto">
          Create a workspace, connect AWS and GitHub, and use your 3 free PRs to
          try Infralyst on real infrastructure.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg">
            <Link href="/signup">Start free with 3 PRs</Link>
          </Button>
          <Button variant="outline" size="lg" asChild>
            <a href={CALENDLY_COST_REVIEW_URL} target="_blank" rel="noopener noreferrer">
              Book a demo
            </a>
          </Button>
        </div>
        <p className="text-sm text-surface-foreground/60 mt-4">
          No credit card required · Read-only IAM role · 30-day money-back
          guarantee
        </p>
      </div>
    </section>
  );
}

export default function Show(): React.ReactElement {
  return (
    <Layout
      title="Pricing"
      description="Simple, transparent pricing for AWS cost optimization. Start free with 3 PRs, upgrade to unlimited when you need more."
    >
      <PricingHero />
      <PlanCards />
      <HowBillingWorks />
      <EverythingYouGet />
      <WhyProPaysForItself />
      <FAQ items={FAQ_ITEMS} title="Pricing FAQ" highlightWord="FAQ" className="py-16 px-4" />
      <FinalCTA />
    </Layout>
  );
}
